/**
 * Generic in-memory lock mechanism to prevent concurrent execution
 * on the same resource key. Operations are queued and executed sequentially.
 */
class LockManager {
  private queues = new Map<string, Promise<unknown>[]>()

  /**
   * Executes a function with a lock on the specified key
   * Operations are queued and executed sequentially (one after another)
   *
   * @param key The unique key to lock on
   * @param operation The async operation to execute
   * @returns Promise that resolves with the operation result
   */
  async withLock<T>(key: string, operation: () => Promise<T>): Promise<T> {
    // Get or create the queue for this key
    if (!this.queues.has(key)) {
      this.queues.set(key, [])
    }

    const queue = this.queues.get(key)!

    // Get the previous operation in the queue (if any)
    const previousOperation = queue[queue.length - 1]

    // Create a promise for this operation
    const operationPromise = this.executeOperation(key, operation, previousOperation)

    // Add this operation to the queue
    queue.push(operationPromise)

    return operationPromise
  }

  private async executeOperation<T>(
    key: string,
    operation: () => Promise<T>,
    previousOperation?: Promise<unknown>
  ): Promise<T> {
    try {
      // Wait for the previous operation to complete (if any)
      if (previousOperation) {
        try {
          await previousOperation
        } catch {
          // Ignore errors from the previous operation
        }
      }

      // Execute this operation
      const result = await operation()
      return result
    } finally {
      // Clean up the queue when operation completes
      this.cleanupQueue(key)
    }
  }

  private cleanupQueue(key: string): void {
    const queue = this.queues.get(key)
    if (queue && queue.length > 0) {
      // Remove the first (completed) operation from the queue
      queue.shift()

      // If queue is empty, remove it entirely
      if (queue.length === 0) {
        this.queues.delete(key)
      }
    }
  }

  /**
   * Check if a key is currently locked (has operations in queue)
   * @param key The key to check
   * @returns True if the key has operations in queue
   */
  isLocked(key: string): boolean {
    const queue = this.queues.get(key)
    return queue ? queue.length > 0 : false
  }

  /**
   * Get the number of operations in queue for a specific key
   * @param key The key to check
   * @returns Number of operations queued for this key
   */
  getQueueLength(key: string): number {
    const queue = this.queues.get(key)
    return queue ? queue.length : 0
  }

  /**
   * Get the total number of operations across all queues
   * @returns Total number of operations currently queued
   */
  getTotalQueuedOperations(): number {
    let total = 0
    for (const queue of this.queues.values()) {
      total += queue.length
    }
    return total
  }

  /**
   * Get all currently locked keys
   * @returns Array of keys that have operations in queue
   */
  getLockedKeys(): string[] {
    const lockedKeys: string[] = []
    for (const [key, queue] of this.queues.entries()) {
      if (queue.length > 0) {
        lockedKeys.push(key)
      }
    }
    return lockedKeys
  }

  /**
   * Get queue information for all keys
   * @returns Object mapping keys to their queue lengths
   */
  getQueueStatus(): Record<string, number> {
    const status: Record<string, number> = {}
    for (const [key, queue] of this.queues.entries()) {
      if (queue.length > 0) {
        status[key] = queue.length
      }
    }
    return status
  }

  /**
   * Clear all queues (useful for testing or emergency cleanup)
   * Note: This doesn't cancel running operations, just removes the queue tracking
   */
  clearAllQueues(): void {
    this.queues.clear()
  }
}

// Export a singleton instance for use across the application
export const lockManager = new LockManager()

// Export the class for testing or custom instances
export { LockManager }
